import React, { useMemo, useState } from 'react';
import { Calendar, ChevronRight } from 'lucide-react';
import { HistoricalVisitListProps, HistoricalVisit } from '../types';
import { DocStatusPill } from '../../../common/Pills';
import { codeLabel } from './StructuredEntryFields';
import UnsyncedWorkHint from '../../../offline/UnsyncedWorkHint';

/** CHT-08: red badge for a moderate-or-worse incident charted on the visit */
const IncidentSeverityBadge: React.FC<{ severity?: string }> = ({ severity }) => {
  if (!severity || severity === 'LOW') return null;
  const cls = severity === 'CRITICAL' ? 'bg-red-600 text-white border-red-700'
    : severity === 'HIGH' ? 'bg-red-100 text-red-800 border-red-300'
    : 'bg-amber-100 text-amber-800 border-amber-300';
  return (
    <span data-testid="visit-incident-severity" className={`rounded border px-1.5 py-0.5 text-xs font-semibold ${cls}`}>
      {codeLabel(severity)} incident
    </span>
  );
};

/** Phase 18U U15: chart-status filter chips */
export const CHART_STATUS_FILTERS = [
  { key: 'ALL', label: 'All' },
  { key: 'DRAFT', label: 'Draft' },
  { key: 'SUBMITTED', label: 'Submitted' },
  { key: 'QA', label: 'In QA' },
  { key: 'COMPLETE', label: 'Complete' },
] as const;

export const filterVisits = (visits: HistoricalVisit[], status: string, discipline: string): HistoricalVisit[] =>
  visits.filter((v) => (status === 'ALL' || (v.chartingStatus || 'DRAFT') === status)
    && (!discipline || v.discipline === discipline));

/**
 * Patient visit history. Phase 18U U15: each row is a button (keyboard reachable) showing the
 * date, the clinician, the visit type, the chart status (not the visit status) and flags; chips
 * filter by chart status and a select by discipline.
 */
const HistoricalVisitList: React.FC<HistoricalVisitListProps> = ({
  historicalVisits,
  handleExpandVisit,
}) => {
  const [status, setStatus] = useState<string>('ALL');
  const [discipline, setDiscipline] = useState('');
  const disciplines = useMemo(() => Array.from(new Set(historicalVisits.map((v) => v.discipline).filter((d): d is string => Boolean(d)))), [historicalVisits]);
  const shown = filterVisits(historicalVisits, status, discipline);

  return (
    <div className="mt-0">
      {/* Phase 16C C10: visits that ended offline on a phone are listed once it syncs */}
      <UnsyncedWorkHint className="mb-2" />
      {historicalVisits.length > 0 && (
        <div className="mb-2 flex flex-wrap items-center gap-2" role="group" aria-label="Filter visits">
          {CHART_STATUS_FILTERS.map((f) => (
            <button key={f.key} type="button" aria-pressed={status === f.key} onClick={() => setStatus(f.key)}
              className={`min-h-[36px] rounded-full border px-3 text-sm font-medium ${status === f.key ? 'border-slate-800 bg-slate-800 text-white' : 'border-gray-300 bg-white text-gray-700'}`}>
              {f.label}
            </button>
          ))}
          {disciplines.length > 1 && (
            <label className="ml-auto flex items-center gap-1 text-sm text-gray-700">
              <span>Discipline</span>
              <select value={discipline} onChange={(e) => setDiscipline(e.target.value)} className="min-h-[36px] rounded border border-gray-300 bg-white px-2 text-sm">
                <option value="">All</option>
                {disciplines.map((d) => <option key={d} value={d}>{d}</option>)}
              </select>
            </label>
          )}
        </div>
      )}
      {/* Desktop header */}
      <div className="mb-2 hidden rounded-lg border border-gray-200 bg-gray-50 md:block" aria-hidden="true">
        <div className="grid grid-cols-[8rem_minmax(0,1fr)_minmax(0,1fr)_7rem_minmax(0,1fr)_1.5rem] items-center gap-3 px-4 py-2 text-sm font-semibold text-gray-700">
          <div>Date</div>
          <div>Clinician</div>
          <div>Visit type</div>
          <div>Chart</div>
          <div>Flags</div>
          <div />
        </div>
      </div>

      {historicalVisits.length === 0 ? (
        <div className="py-8 text-center">
          <Calendar size={48} className="mx-auto mb-4 text-gray-300" aria-hidden="true" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">No historical visits available</h3>
          <p className="text-gray-600">Historical visits will appear here once they are available.</p>
        </div>
      ) : shown.length === 0 ? (
        <p className="py-4 text-center text-sm text-gray-600" data-testid="visit-filter-empty">No visits on this page match the filter.</p>
      ) : (
        <ul className="space-y-1.5">
          {shown.map((visit) => (
            <li key={visit.id}>
              <button
                type="button"
                onClick={() => handleExpandVisit(visit)}
                data-testid="visit-row"
                className="w-full rounded-lg border border-gray-200 bg-white text-left transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-600"
              >
                <div className="hidden grid-cols-[8rem_minmax(0,1fr)_minmax(0,1fr)_7rem_minmax(0,1fr)_1.5rem] items-center gap-3 px-4 py-2 md:grid">
                  <span className="flex items-center text-sm font-medium"><Calendar size={16} className="mr-2 text-gray-500" aria-hidden="true" />{visit.date}</span>
                  <span className="truncate text-sm text-gray-800">{visit.providerName || '—'}</span>
                  <span className="truncate text-sm text-gray-700">{visit.type}</span>
                  <span><DocStatusPill status={visit.chartingStatus || 'DRAFT'} /></span>
                  <span className="flex flex-wrap gap-1"><IncidentSeverityBadge severity={visit.maxIncidentSeverity} /></span>
                  <ChevronRight size={16} className="text-gray-500" aria-hidden="true" />
                </div>
                <div className="flex min-h-[56px] items-center justify-between gap-2 p-3 md:hidden">
                  <span className="min-w-0">
                    <span className="block text-sm font-medium text-gray-900">{visit.date} · {visit.type}</span>
                    <span className="block truncate text-xs text-gray-600">{visit.providerName || 'Clinician not recorded'}</span>
                    <span className="mt-1 flex flex-wrap items-center gap-1">
                      <DocStatusPill status={visit.chartingStatus || 'DRAFT'} />
                      <IncidentSeverityBadge severity={visit.maxIncidentSeverity} />
                    </span>
                  </span>
                  <ChevronRight size={18} className="shrink-0 text-gray-500" aria-hidden="true" />
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="mt-3 text-center text-xs text-gray-600">
        Open a visit to see or review its chart.
      </div>
    </div>
  );
};

export default HistoricalVisitList;
